import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listInvoices, payInvoice } from '../../services/portal'
import type { PortalInvoice } from '../../types'

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', sent: 'Sent', paid: 'Paid', void: 'Void', refunded: 'Refunded',
}

const STATUS_STYLE: Record<string, string> = {
  draft:    'bg-nala-surface text-nala-mute border border-nala-line',
  sent:     'bg-amber-50 text-amber-800 border border-amber-200',
  paid:     'bg-nala-soft text-nala-accent border border-nala-line',
  void:     'bg-nala-surface text-nala-mute border border-nala-line',
  refunded: 'bg-nala-surface text-nala-mute border border-nala-line',
}

function fmtMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`
}

export default function PortalInvoicesPage() {
  const [scope, setScope] = useState<'all' | 'outstanding' | 'paid'>('all')
  const qc = useQueryClient()

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['portal', 'invoices', scope],
    queryFn: () => listInvoices(scope === 'all' ? undefined : scope),
  })

  const pay = useMutation({
    mutationFn: (id: number) => payInvoice(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['portal', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['portal', 'dashboard'] })
      if (data.checkout_url) window.open(data.checkout_url, '_blank')
    },
  })

  const outstandingTotal = invoices
    .filter((inv) => ['draft', 'sent'].includes(inv.status))
    .reduce((sum, inv) => sum + (inv.total_cents - inv.amount_paid_cents), 0)

  return (
    <div className="space-y-7">
      {/* Header */}
      <div>
        <h1 className="font-fraunces text-4xl font-[400] tracking-tight text-nala-ink">
          Invoices
        </h1>
        <p className="text-nala-mute mt-1.5 text-[15px]">Pay outstanding balances and review payment history.</p>
      </div>

      {/* Outstanding total callout */}
      {scope !== 'paid' && outstandingTotal > 0 && (
        <div className="bg-white border border-nala-clay/30 rounded-2xl px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-nala-mute">Balance due</p>
            <p className="font-fraunces text-3xl font-[500] text-nala-clay leading-tight mt-0.5">
              {fmtMoney(outstandingTotal)}
            </p>
          </div>
          <div className="text-sm text-nala-mute">across {invoices.filter((i) => ['draft', 'sent'].includes(i.status)).length} invoice(s)</div>
        </div>
      )}

      {/* Scope toggle */}
      <div className="inline-flex bg-white border border-nala-line rounded-full p-1 gap-1">
        {(['all', 'outstanding', 'paid'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`px-5 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${
              scope === s
                ? 'bg-nala-accent text-nala-accentInk shadow-sm'
                : 'text-nala-mute hover:text-nala-ink'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="py-12 text-center">
          <p className="font-fraunces text-lg italic text-nala-mute">Loading…</p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white border border-nala-line rounded-3xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-nala-soft flex items-center justify-center text-3xl mx-auto mb-4">🧾</div>
          <h3 className="font-fraunces text-2xl font-[400] text-nala-ink">No invoices</h3>
          <p className="text-nala-mute mt-2 text-sm">You're all caught up.</p>
        </div>
      ) : (
        <div className="bg-white border border-nala-line rounded-2xl overflow-hidden">
          <ul className="divide-y divide-nala-line">
            {invoices.map((inv) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                onPay={() => pay.mutate(inv.id)}
                paying={pay.isPending && pay.variables === inv.id}
              />
            ))}
          </ul>
        </div>
      )}

      {pay.isError && (
        <p className="text-sm text-red-600">Payment couldn't start. Please try again or contact support.</p>
      )}
    </div>
  )
}

function InvoiceRow({ invoice, onPay, paying }: { invoice: PortalInvoice; onPay: () => void; paying: boolean }) {
  const owed = invoice.total_cents - invoice.amount_paid_cents
  const canPay = ['draft', 'sent'].includes(invoice.status) && owed > 0

  return (
    <li className="px-6 py-5 flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap mb-1">
          <span className="font-fraunces text-lg font-[500] text-nala-ink">{invoice.number}</span>
          <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${STATUS_STYLE[invoice.status] ?? 'bg-nala-surface text-nala-mute'}`}>
            {STATUS_LABEL[invoice.status] ?? invoice.status}
          </span>
          {invoice.overdue && (
            <span className="text-[11px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wide bg-red-50 text-red-700 border border-red-100">
              Overdue
            </span>
          )}
        </div>
        <p className="text-sm text-nala-mute">
          {invoice.due_at ? `Due ${new Date(invoice.due_at).toLocaleDateString()}` : 'No due date'}
        </p>
      </div>

      <div className="text-right shrink-0">
        <div className="font-fraunces text-xl font-[500] text-nala-ink">{fmtMoney(owed)}</div>
        {invoice.amount_paid_cents > 0 && invoice.amount_paid_cents < invoice.total_cents && (
          <div className="text-xs text-nala-mute mt-0.5">
            of {fmtMoney(invoice.total_cents)} total
          </div>
        )}
      </div>

      {canPay && (
        <button
          onClick={onPay}
          disabled={paying}
          className="ml-4 shrink-0 bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold rounded-full px-5 py-2 text-sm disabled:opacity-60 transition"
        >
          {paying ? 'Opening…' : 'Pay now'}
        </button>
      )}
    </li>
  )
}
